import { ModalVideoListVideo } from 'lib/components';
import { VideoEmailStatus, VideoPages } from 'lib/const';
import { AutomotiveRole } from 'lib/const/AutomotiveRole';
import { VideoListAutomotiveItem, VideoRequest, useAuth } from 'lib/context';
import { theme } from 'lib/style';
import React, { useState } from 'react';
import { BsPinAngleFill } from 'react-icons/bs';
import { MdBarChart, MdLock } from 'react-icons/md';
import { useHistory } from 'react-router';
import styled, { useTheme } from 'styled-components/macro';
import { VideoItemActions, VideoItemStatus, VideoItemThumbnail } from '.';

import { RepairOrderItem } from 'lib/api/repairOrders/types';
import { STANDARD_DATE_FORMAT } from 'lib/const/DateFormat';
import {
  checkIfUserIsServiceManager,
  checkIfUserIsTechnician,
} from 'lib/utils/automotiveRolePermissionChecks';
import { checkIfFreemium } from 'lib/utils/functions';
import { VideoLimitStatus } from 'lib/utils/productFeature';
import { useTranslation } from 'react-i18next';
import { serverTimeToUserTime } from 'lib/utils/time';
import { FOLDER_ACCESS } from '../videoList/constants';
import selectors from '../../../../../cypress/selectors';
import { FOLDER_TYPES, FoldersV2 } from 'lib/api/folders/getFoldersV2';
import { VideoTrimMergeModal } from 'lib/components/modal/VideoTrimMergeModal';
import { StatusBadge, Tag } from 'react-covideo-common';
import { useGTMAnalytics } from 'lib/hooks/GTM/useGTMAnalytics';
import {
  CovideoGTMVehicleItem,
  CovideoGTMVideoAttributes,
} from 'lib/hooks/GTM/types';
import { GTMVideoInteractionActions } from 'lib/hooks/GTM/actions/video';

const Row = styled.div`
  display: flex;
  align-items: stretch;
  flex-grow: 1;
  padding: 16px 0;
  margin-bottom: 8px;
  border-bottom: 1px solid ${theme.palette.secondaryButtonBlueBg};
`;
const Left = styled.div`
  width: 130px;
  flex-shrink: 0;
`;
const Center = styled.div`
  width: 100%;
  margin-left: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
const Right = styled.div`
  flex-shrink: 0;
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
const Info = styled.div`
  color: ${theme.palette.gray80};
  font-weight: normal;
  font-size: 13px;
  line-height: 24px;
  > b {
    font-weight: 500;
  }
`;
const RONumber = styled.div`
  font-size: 13px;
  line-height: 24px;
  color: ${theme.palette.gray80};
  > b {
    font-weight: 500;
  }
`;
const ViewCounter = styled.div`
  display: flex;
  align-items: center;
  font-size: 13px;
  line-height: 24px;
  color: ${theme.palette.gray80};
  cursor: pointer;
  > span {
    margin-left: 2px;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
`;
type TitleProps = {
  clickable: boolean;
};
const Title = styled.h3<TitleProps>`
  color: ${theme.palette.gray100};
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  margin: 0;
  cursor: ${props => props.clickable && 'pointer'};
`;
const Footer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;
const Bull = styled.span`
  :after {
    content: '•';
    margin: 0 6px;
    color: ${theme.palette.gray80};
  }
`;

type UserAdvisor = {
  firstName: string;
  lastName: string;
};
export enum VideoItemType {
  Video = 'video',
  VideoRequest = 'videoRequest',
}
type Props = {
  videoItemType: VideoItemType;
  recordedBy: UserAdvisor;
  assignedTo: UserAdvisor;
  video: VideoListAutomotiveItem;
  videoRequest?: VideoRequest;
  repairOrder?: RepairOrderItem;
  videoLimitStatus?: VideoLimitStatus;
  handleReassignSubmit: (
    advisorId: string,
    videoIds: string[],
    advisor: any
  ) => void;
  handleMerge?: (videoIds: string) => void;
  handleDownload?: (downloadData: { videoId: string; title: string }[]) => void;
  handleDuplicate?: (video: any) => void;
  handleMoveCopyButtonClick?: (videoIds: string[], folder?: FoldersV2) => void;
  refreshVideoList?: () => void;
  disablePinOption?: boolean;
  currentFolder?: FoldersV2 | null;
  isFolderShared?: boolean;
  handleOnMarkAsSentSuccessCallback?: () => void;
  setSelectedVideos?: React.Dispatch<
    React.SetStateAction<VideoListAutomotiveItem | null>
  >;
};
export const VideoItem = ({
  videoItemType,
  recordedBy,
  assignedTo,
  video,
  videoRequest,
  videoLimitStatus,
  handleReassignSubmit,
  handleMerge,
  handleDownload,
  handleDuplicate,
  repairOrder,
  handleMoveCopyButtonClick,
  refreshVideoList,
  disablePinOption,
  currentFolder = null,
  isFolderShared = false,
  handleOnMarkAsSentSuccessCallback,
  setSelectedVideos,
}: Props) => {
  const { t } = useTranslation();
  const [showMoreTags, setShowMoreTags] = useState(false);
  const [showModalPreviewVideo, setShowModalPreviewVideo] = useState(false);
  const [showModalQuickShareMarkAsSent, setShowModalQuickShareMarkAsSent] =
    useState(false);
  const [showVideoMoreOptionsModal, setShowVideoMoreOptionsModal] =
    useState(false);
  const { userData } = useAuth();
  const { colors } = useTheme();
  const { trialExpired } = userData;
  const history = useHistory();
  const isFreemium = checkIfFreemium(userData);
  const isUserServiceTechnician = checkIfUserIsTechnician(userData);
  const { trackGTMVideoInteractionEvent } = useGTMAnalytics();

  const createGTMVideoInteractionEvent = async ({
    action,
  }: {
    action: GTMVideoInteractionActions;
  }) => {
    const gtmVideoAttributes: CovideoGTMVideoAttributes = {
      videoId: Number(video.id),
      videoType: video?.videoRequest?.videoType,
      videoCustomerName: !!video?.videoRequest?.customerName,
      videoLength: video.videoLength,
      videoTitle: video.title,
    };
    await trackGTMVideoInteractionEvent?.({
      videoAttributes: gtmVideoAttributes,
      action: action,
      vehicle: video?.videoRequest?.inventoryItem as CovideoGTMVehicleItem,
    });
  };

  const handleVideoThumbnailClick = async () => {
    await createGTMVideoInteractionEvent({
      action: GTMVideoInteractionActions.VIEW,
    });
    setShowModalPreviewVideo(true);
  };

  const handleAnalyticsClick = () => {
    if (trialExpired) {
      return;
    }
    history.push(`/classic/edit-video/${video.id}/${VideoPages.ANALYTICS}`);
  };

  const isTechnician = userData.automotiveRole === AutomotiveRole.TECHNICIAN;
  const isManager = checkIfUserIsServiceManager(userData);

  const advisorInfo = isManager ? (
    <>
      Advisor:{' '}
      <b>
        {assignedTo ? assignedTo.firstName + ' ' + assignedTo.lastName : ''}
      </b>
    </>
  ) : (
    <>
      Assigned to{' '}
      <b>
        {assignedTo ? assignedTo.firstName + ' ' + assignedTo.lastName : ''}
      </b>
    </>
  );

  const showRecordedBy = () => {
    const isFolderShared = currentFolder?.folderType !== FOLDER_TYPES.PERSONAL;
    return isManager || isFolderShared;
  };

  const recordedInfo = showRecordedBy() ? (
    <>
      Recorded by:{' '}
      <b>
        {recordedBy ? recordedBy.firstName + ' ' + recordedBy.lastName : ''}
      </b>
    </>
  ) : (
    <></>
  );

  const handleViewDetailsClick = async () => {
    await createGTMVideoInteractionEvent({
      action: GTMVideoInteractionActions.EDIT_VIDEO,
    });
    if (currentFolder?.folderType !== FOLDER_TYPES.PERSONAL) {
      setShowVideoMoreOptionsModal(true);
    } else {
      if (trialExpired || isUserServiceTechnician) {
        return;
      }
      videoRequest?.emailStatus !== VideoEmailStatus.Deleted &&
        history.push(
          `/classic/edit-video/${video.id}/${VideoPages.DETAILS}?folderId=${currentFolder?.folderId}`
        );
    }
  };

  const onVideoMoreOptionsSubmit = () => {
    if (trialExpired || isUserServiceTechnician) {
      return;
    }
    videoRequest?.emailStatus !== VideoEmailStatus.Deleted &&
      history.push(
        `/classic/edit-video/${video.id}/${VideoPages.DETAILS}?folderId=${currentFolder?.folderId}`
      );
  };

  const clientTime = serverTimeToUserTime(
    video.recordDate,
    userData,
    `${STANDARD_DATE_FORMAT} hh:mm:ss A`,
    false
  );

  return (
    <>
      <Row>
        <Left>
          <VideoItemThumbnail
            handleClick={() => handleVideoThumbnailClick()}
            videoId={video.id}
            thumbnail={video.thumbnail}
            duration={video.videoLength || 0}
            emailStatus={videoRequest?.emailStatus}
            processing={video.processing}
            refreshVideoList={refreshVideoList}
          />
          {!isFreemium &&
            (videoRequest?.status ? (
              <VideoItemStatus
                videoId={video.id}
                status={videoRequest?.status}
                emailStatus={videoRequest.emailStatus as VideoEmailStatus}
                videoItemType={videoItemType}
                automotiveRole={userData.automotiveRole}
              />
            ) : (
              <VideoItemStatus videoId={video.id} />
            ))}
        </Left>
        <Center>
          <TitleWrapper>
            {!!video.pinnedVideo?.pinnedAt && (
              <>
                <BsPinAngleFill color={colors.primary[100]} size={18} /> &nbsp;
              </>
            )}
            {video.protected.toString() === '1' && (
              <MdLock
                onClick={() => {
                  if (trialExpired) {
                    return;
                  }
                  history.push(`/classic/edit-video/${video.id}/security`);
                }}
                opacity={0.5}
              />
            )}
            <Title
              onClick={handleViewDetailsClick}
              clickable={
                videoRequest?.emailStatus !== VideoEmailStatus.Deleted &&
                !isUserServiceTechnician
              }
              data-cy={selectors.libraryPage.videoTitle}
            >
              {/* SUS-797 changes */}
              {video.title.length > 60
                ? video.title.substring(0, 60) + '...'
                : video.title}
            </Title>
          </TitleWrapper>

          <Info>
            {isTechnician ? advisorInfo : recordedInfo}
            {(showRecordedBy() || isTechnician) && <Bull />}
            {clientTime}
            {isManager ? (
              <>
                <Bull />
                {advisorInfo}
              </>
            ) : null}
          </Info>
          <Footer>
            {userData.isAutomotiveSalesRole && videoRequest?.vin && (
              <>
                <RONumber>
                  <b>VIN:</b> {videoRequest.vin}
                </RONumber>
              </>
            )}
            {userData.isAutomotiveServiceRole &&
              videoRequest?.repairOrderNumber && (
                <>
                  <RONumber>
                    <b>RO#:</b> {videoRequest.repairOrderNumber}
                  </RONumber>
                </>
              )}
            {videoItemType === VideoItemType.Video && !isTechnician && (
              <>
                {/*If we have vin for sales role or we have ro number for service role - show bull*/}
                {((userData.isAutomotiveSalesRole && videoRequest?.vin) ||
                  (userData.isAutomotiveServiceRole &&
                    videoRequest?.repairOrderNumber)) && <Bull />}
                <ViewCounter onClick={handleAnalyticsClick}>
                  <MdBarChart color={theme.palette.gray60} size={18} />
                  <span>{t('view', { count: parseInt(video.views) })}</span>
                </ViewCounter>
              </>
            )}
            {!!video.isArchived && (
              <>
                <Bull />
                <StatusBadge
                  text='Archived'
                  size='small'
                  extendStyles={{
                    container: { background: colors.neutral[60] },
                    text: { color: colors.neutral[10] },
                  }}
                />
              </>
            )}
            {video.tags && !!video.tags.length && (
              <>
                <Bull />
                {video.tags.slice(0, 2).map(tag => (
                  <Tag
                    key={'videotag-' + tag.tagId}
                    tag={{ value: tag.tagId, label: tag.tag }}
                    // to do refactor footer video item to use gap
                    extendStyles={{
                      container: { marginRight: '4px', marginBottom: '4px' },
                    }}
                    size='small'
                  />
                ))}
                {!showMoreTags && video.tags.length > 2 && (
                  <div onClick={() => setShowMoreTags(true)}>
                    <Tag
                      // to do refactor footer video item to use gap
                      extendStyles={{
                        container: {
                          cursor: 'pointer',
                          marginRight: '4px',
                          marginBottom: '4px',
                        },
                      }}
                      tag={{
                        label: `${video.tags.length - 2}`,
                        value: 'show more',
                      }}
                      size='small'
                    />
                  </div>
                )}
                {showMoreTags &&
                  video.tags.length > 2 &&
                  video.tags.slice(2).map(tag => (
                    <Tag
                      key={'videotag-' + tag.tagId}
                      tag={{ value: tag.tagId, label: tag.tag }}
                      // to do refactor footer video item to use gap
                      extendStyles={{
                        container: { marginRight: '4px', marginBottom: '4px' },
                      }}
                      size='small'
                    />
                  ))}
              </>
            )}
          </Footer>
        </Center>
        {video.access !== FOLDER_ACCESS.VIEW && (
          <Right>
            <VideoItemActions
              videoItemType={videoItemType}
              videoRequest={videoRequest}
              repairOrder={repairOrder}
              video={video}
              setShowModalQuickShareMarkAsSent={
                setShowModalQuickShareMarkAsSent
              }
              handleReassignSubmit={handleReassignSubmit}
              handleMerge={handleMerge}
              handleDownload={handleDownload}
              handleDuplicate={handleDuplicate}
              handleAnalyticsClick={handleAnalyticsClick}
              createGTMVideoInteractionEvent={createGTMVideoInteractionEvent}
              handleMoveCopyButtonClick={handleMoveCopyButtonClick}
              refreshVideoList={refreshVideoList}
              setSelectedVideos={setSelectedVideos}
              videoLimitStatus={videoLimitStatus}
              disablePinOption={disablePinOption}
              currentFolder={currentFolder}
              isFolderShared={isFolderShared}
              showModalQuickShareMarkAsSent={showModalQuickShareMarkAsSent}
              handleOnMarkAsSentSuccessCallback={
                handleOnMarkAsSentSuccessCallback
              }
            />
          </Right>
        )}
      </Row>
      {showModalPreviewVideo && (
        <ModalVideoListVideo
          videoId={video.id}
          handleModalClose={() => setShowModalPreviewVideo(false)}
        />
      )}
      {showVideoMoreOptionsModal && (
        <VideoTrimMergeModal
          handleModalClose={() => setShowVideoMoreOptionsModal(false)}
          handleModalSubmit={() => onVideoMoreOptionsSubmit()}
          headerText={'You are about to enter more options'}
          content={
            'You are about to enter More Options. Any actions taken within the editor will affect the original video in this folder.'
          }
          submitButtonText={`Edit Video`}
        />
      )}
    </>
  );
};
